package com.exura.components.inputs

import androidx.compose.runtime.Composable
import com.exura.components.icons.InlineIcon
import com.exura.components.icons.UntiledIcon
import com.exura.primary
import com.exura.surface
import com.exura.ui.Dimens
import com.exura.ui.Durations
import com.exura.ui.HtmlProperties
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.LineStyle

data class ModeToggleOption<T>(
    val mode: T,
    val label: String,
    val iconSrc: String
)

@Composable
fun <T> ModeToggleGroup(
    selectedMode: T,
    modes: List<ModeToggleOption<T>>,
    onModeChange: (T) -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = ModeToggleGroupStyle.toModifier().then(modifier),
        horizontalArrangement = Arrangement.spacedBy(Dimens.spacingNone),
    ) {
        modes.forEach { item ->
            Box(
                modifier = ModeToggleOptionStyle.toModifier(
                    if (item.mode == selectedMode) ModeToggleOptionSelectedVariant else null
                    )
                    .weight(1f)
                    .onClick { onModeChange(item.mode) },
                contentAlignment = Alignment.Center
            ) {
                InlineIcon(
                    icon = { UntiledIcon(item.iconSrc) }
                ) {
                    SpanText(item.label)
                }
            }
        }
    }
}

sealed interface ModeToggleOptionKind : ComponentKind

val ModeToggleGroupStyle = CssStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .borderRadius(Dimens.modeToggleBorderRadius)
            .border {
                width(Dimens.separatorWidthDefault)
                style(LineStyle.Solid)
                color(palette.border)
            }
    }
}

val ModeToggleOptionStyle = CssStyle<ModeToggleOptionKind> {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .padding(Dimens.modeTogglePadding)
            .borderRadius(Dimens.modeToggleBorderRadius)
            .cursor(Cursor.Pointer)
            .transition(
                Transition.of(
                    property = HtmlProperties.ALL,
                    duration = Durations.short,
                    timingFunction = AnimationTimingFunction.EaseInOut
                )
            )
    }
    hover {
        Modifier
            .backgroundColor(palette.surface)
    }
}

val ModeToggleOptionSelectedVariant = ModeToggleOptionStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .backgroundColor(palette.primary.toRgb().copyf(alpha = 0.2f))
    }
    hover {
        Modifier
            .backgroundColor(palette.primary.toRgb().copyf(alpha = 0.1f))
    }
}
